import { ref, computed } from 'vue'
import { miniStore } from '@/miniStore'
import { MessageBox } from '@qs/ui'
import { SIGN_STATUS_ENUM } from '../config'
import {
  getLeaseSignProcessStatus,
  getLeaseSignProcessConfig,
  genSignQrCode,
  revokeApproval,
  revokeSign,
  confirmContract,
} from '@/api/domain/orders'
import { useGetOrderStepFromDetail } from '@/pages/orders_new/hooks/index.js'
import { CATEGORY_CODE_MAP } from '@/config/rentConfig.js'
import { useESignRender } from '@/hooks/useESignRender'

let onRefreshCallback = () => {
  // 默认空实现，避免未设置回调时调用报错
}

export const setOnRefreshCallback = (cb) => {
  onRefreshCallback = cb
}

export const triggerRefresh = () => {
  if (typeof onRefreshCallback === 'function') {
    onRefreshCallback()
  }
}

export function useOrderAPI({ id } = {}) {
  const getOrderId = () => miniStore.state.data.global.orderId || id

  const submitSignContract = async (cb) => {
    const orderId = getOrderId()
    const {
      licenceNo,
      isWithhold,
      signChannel,
      attachment,
      customerId,
    } = miniStore.state.data.step1
    try {
      const { code: confirmCode } = await confirmContract({
        orderId,
        isWithhold,
        licenceNo,
        signChannel,
        attachment: attachment && attachment.length ? attachment[0].url : '',
        customerId,
      })
      if (confirmCode === 1) {
        const { getAndGotoStep } = useGetOrderStepFromDetail(orderId)
        getAndGotoStep()
      }
    } catch (err) {
      console.error(err)
    } finally {
      cb?.()
    }
  }

  const queryOrderStatus = async () => {
    const orderId = getOrderId()
    const { code, data } = await getLeaseSignProcessStatus({
      orderId,
    })
    if (code === 1) {
      // 批量更新 store 数据
      miniStore.dispatch({
        type: 'BATCH_UPDATE_FIELD',
        payload: {
          updates: {
            step1: {
              flowState: data.flowState,
              hasAlipay: data.hasAlipay,
              signQrCode: data?.signQrCode?.qrCode,
              alipayQrCode: data?.alipayQrCode?.qrCode,
              plateNo: data?.signQrCode?.plateNo || data?.alipayQrCode?.plateNo || '',
            },
          },
        },
      })

      if (data.flowState === SIGN_STATUS_ENUM.SIGN_COMPLETED) {
        submitSignContract()
      }
    }

    return { code, data }
  }

  const queryOrderConfig = async () => {
    const orderId = getOrderId()
    const { licenceNo } = miniStore.state.data.step1
    const { rentCategory } = miniStore.state.data.global.orderDetail
    const { code, data } = await getLeaseSignProcessConfig({
      orderId,
      licenceNo,
    })

    if (code === 1) {
      const rawEnable = data.enablePayMethods || []
      const normalizedEnable = (rentCategory === CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
        ? rawEnable.filter((item) => Number(item) !== 50) : rawEnable).map((v) => Number(v))

      const normalizedPay = (data.payMethods || []).map((v) => Number(v))

      miniStore.dispatch({
        type: 'BATCH_UPDATE_FIELD',
        payload: {
          updates: {
            step1: {
              signChannel: data.signChannel,
              licenceNo: data.licenceNo || '',
              licenceNoFixed: data.licenceNoFixed,
              isWithhold: data.isWithhold,
              payMethods: normalizedPay,
              enablePayMethods: normalizedEnable,
              type: data.type,
              periodLimit: data.periodLimit,
              periodLimitType: data.periodLimit ? 'limited' : 'unlimited',
              spId: data.spId,
              spFixed: data.spFixed,
              rentCategory: data.rentCategory,
              customerId: data.customerId,
              weeklyDeductionDay: data.startDate || '',
              hasCustomer: data.hasCustomer,
            },
          },
        },
      })
    }
  }

  const genQrCode = () => {
    const {
      licenceNo,
      isWithhold,
      payMethods,
      type,
      periodLimit,
      spId,
      weeklyDeductionDay,
    } = miniStore.state.data.step1
    const { fetchESignInfo } = useESignRender()

    return new Promise((resolve) => {
      fetchESignInfo({
        type: 'dialog',
        params: {
          licenceNo,
        },
        success: async () => {
          try {
            const orderId = getOrderId()
            const { code, data } = await genSignQrCode({
              orderId,
              licenceNo,
              isWithhold,
              payMethods,
              type,
              periodLimit,
              spId,
              startDate: weeklyDeductionDay,
            })
            if (code === 1) {
              const { signQrCode, alipayQrCode, flowState, hasAlipay } = data
              miniStore.dispatch({
                type: 'BATCH_UPDATE_FIELD',
                payload: {
                  updates: {
                    step1: {
                      signQrCode: signQrCode?.qrCode || '',
                      alipayQrCode: alipayQrCode?.qrCode || '',
                      plateNo: signQrCode?.plateNo || '',
                      flowState,
                      hasAlipay,
                    },
                  },
                },
              })
            }
            resolve({ code, data })
          } catch (error) {
            resolve({ code: -1 })
          }
        },
        fail: () => {
          resolve({ code: -1 })
        },
      })
    })
  }

  return {
    queryOrderStatus,
    queryOrderConfig,
    genQrCode,
    submitSignContract,
  }
}

// 撤回签署和撤回审批按钮
export function useWithdrawButton() {
  const loading = ref(false)

  const label = computed(() => {
    const { flowState } = miniStore.state.data.step1 || {}
    if (flowState === SIGN_STATUS_ENUM.PENDING_APPROVAL) {
      return '撤回审批'
    }
    if (flowState === SIGN_STATUS_ENUM.WECHAT_QR_PENDING_SCAN
        || flowState === SIGN_STATUS_ENUM.PENDING_SIGN
    ) {
      return '撤回签署'
    }
    return ''
  })

  const handler = async (cb) => {
    const { flowState } = miniStore.state.data.step1 || {}
    const { orderId } = miniStore.state.data.global
    const { queryOrderStatus, queryOrderConfig } = useOrderAPI({})

    loading.value = true

    if (flowState === SIGN_STATUS_ENUM.PENDING_APPROVAL) {
      MessageBox.confirm('确定要撤回订单的审批吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        const { code } = await revokeApproval({
          orderId,
        })
        loading.value = false
        if (code === 1) {
          triggerRefresh()
          MessageBox.alert(
            '合同签署撤回成功，可重新发起。',
            '消息提示',
            {
              type: 'info',
              confirmButtonText: '知道了',
              showClose: false,
            },
          )
        }
      }).finally(() => {
        loading.value = false
        queryOrderStatus()
        queryOrderConfig()
      })
    }

    if (flowState === SIGN_STATUS_ENUM.WECHAT_QR_PENDING_SCAN
        || flowState === SIGN_STATUS_ENUM.ALIPAY_QR_PENDING_SCAN
        || flowState === SIGN_STATUS_ENUM.PENDING_SIGN
    ) {
      MessageBox.confirm('确定要撤回订单的签署吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        const { code } = await revokeSign({
          orderId,
        })
        loading.value = false
        if (code === 1) {
          triggerRefresh()
          await queryOrderStatus()
          cb?.()

          MessageBox.alert(
            '合同签署撤回成功，可重新发起。',
            '消息提示',
            {
              type: 'info',
              confirmButtonText: '知道了',
              showClose: false,
            },
          )
        }
      }).catch(() => null)
        .finally(() => {
          loading.value = false
          queryOrderStatus()
          queryOrderConfig()
        })
    }
  }

  return {
    label,
    handler,
    loading,
  }
}
